import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Sliders,
  Play,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  FileText,
  Layers,
  KeyRound,
  PlugZap,
  Gauge,
  ShieldAlert,
  Coins,
  Route,
  Bot,
  Activity,
} from 'lucide-react';
import {
  fetchGeoOptimizerDashboard,
  updateGeoOptimizerSettings,
  triggerGeoOptimizerRun,
  applyOptimizerProposal,
  rejectOptimizerProposal,
  rollbackOptimizerProposal,
  applyAllSafeOptimizerProposals,
  fetchConnectionsStatus,
  savePlatformConnection,
  testPlatformConnection,
  fetchOAuthAuthorizeUrl,
  exchangeOAuthCode,
  runOptimizerTier,
  GeoOptimizerDashboardResponse,
  OptimizerGeo,
  OptimizerMode,
  OptimizerTier,
  OptimizerProposal,
  OptimizerPlatform,
  PlatformConnectionInfo,
} from '../services/geoOptimizerApi';

const PINE = '#0F3F34';
const ORANGE = '#F07824';
const MINT = '#F4F8F6';

const PLATFORM_META: Record<OptimizerPlatform, { label: string; bg: string; text: string; border: string }> = {
  google: { label: 'Google Ads', bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200' },
  microsoft: { label: 'Microsoft Ads', bg: 'bg-teal-50', text: 'text-teal-800', border: 'border-teal-200' },
  meta: { label: 'Meta Ads', bg: 'bg-indigo-50', text: 'text-indigo-800', border: 'border-indigo-200' },
};

const PROPOSAL_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  budget_shift: { label: 'Budget Reallocation', color: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  negative_keyword: { label: 'Negative Keyword', color: 'bg-rose-50 text-rose-800 border-rose-200' },
  keyword_promotion: { label: 'Winner Keyword Promotion', color: 'bg-amber-50 text-amber-800 border-amber-200' },
  pause_bleeder: { label: 'Pause Bleeder', color: 'bg-red-100 text-red-900 border-red-300' },
  enable_winner: { label: 'Enable Winner', color: 'bg-green-50 text-green-800 border-green-200' },
  new_rsa: { label: 'Compliant RSA Copy', color: 'bg-purple-50 text-purple-800 border-purple-200' },
  bid_update: { label: 'Bid / tCPA Update', color: 'bg-sky-50 text-sky-800 border-sky-200' },
  landing_url: { label: 'Landing URL Routing', color: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
  campaign_create: { label: 'Campaign Build & Activate', color: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200' },
  ad_pause: { label: 'Creative Fatigue Pause', color: 'bg-orange-50 text-orange-800 border-orange-200' },
  emergency_throttle: { label: 'Drawdown Throttle', color: 'bg-red-50 text-red-800 border-red-200' },
  meta_creative: { label: 'Programmatic Meta Creative', color: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
};

const REGIME_META: Record<string, { label: string; color: string; bar: string }> = {
  normal_scale: { label: 'Normal Scale', color: 'bg-emerald-50 text-emerald-800 border-emerald-200', bar: 'bg-emerald-500' },
  pro_rata_pace: { label: 'Pro-Rata Pacing', color: 'bg-sky-50 text-sky-800 border-sky-200', bar: 'bg-sky-500' },
  loss_buffer_throttle: { label: 'Loss-Buffer Throttle', color: 'bg-amber-50 text-amber-800 border-amber-200', bar: 'bg-amber-500' },
  survival_floor: { label: 'Survival Floor 400 DKK/d', color: 'bg-rose-50 text-rose-800 border-rose-200', bar: 'bg-rose-500' },
};

const TIER_META: Record<OptimizerTier, { label: string; cadence: string; hint: string }> = {
  intraday_guardian: { label: 'Intraday Guardian', cadence: 'every 4 h', hint: 'Spend vs today’s cap, drawdown throttle, acute bleeder pauses' },
  daily_solver: { label: 'Daily Solver', cadence: 'every 24 h', hint: 'P&L envelope, allocation, hygiene, bids, routing, value calibration' },
  creative_strategist: { label: 'Creative Strategist', cadence: 'every 72 h', hint: 'Fatigue audit, cluster coverage, RSAs & Meta creatives' },
};

const dkk = (n?: number | null): string => Math.round(n || 0).toLocaleString();
const pct = (n?: number | null, digits: number = 1): string => `${((n || 0) * 100).toFixed(digits)}%`;

export const SimplyloanGeoOptimizer: React.FC = () => {
  const [geo, setGeo] = useState<OptimizerGeo>('no');
  const [windowDays, setWindowDays] = useState<number>(30);
  const [data, setData] = useState<GeoOptimizerDashboardResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [running, setRunning] = useState<boolean>(false);
  const [savingSettings, setSavingSettings] = useState<boolean>(false);
  const [busyProposalId, setBusyProposalId] = useState<number | null>(null);
  const [tierRunning, setTierRunning] = useState<OptimizerTier | null>(null);
  const [banner, setBanner] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Connection modal state
  const [connections, setConnections] = useState<Record<string, PlatformConnectionInfo> | null>(null);
  const [showConnectModal, setShowConnectModal] = useState<boolean>(false);
  const [connectTab, setConnectTab] = useState<'google' | 'microsoft'>('google');
  const [savingConn, setSavingConn] = useState<boolean>(false);
  const [testingConn, setTestingConn] = useState<boolean>(false);

  // Google form fields
  const [gDevToken, setGDevToken] = useState('');
  const [gClientId, setGClientId] = useState('');
  const [gClientSecret, setGClientSecret] = useState('');
  const [gRefreshToken, setGRefreshToken] = useState('');
  const [gMccId, setGMccId] = useState('');
  const [gCidNo, setGCidNo] = useState('');
  const [gCidSe, setGCidSe] = useState('');
  const [gCidFi, setGCidFi] = useState('');

  // Microsoft form fields
  const [mDevToken, setMDevToken] = useState('');
  const [mClientId, setMClientId] = useState('');
  const [mClientSecret, setMClientSecret] = useState('');
  const [mRefreshToken, setMRefreshToken] = useState('');
  const [mCustomerId, setMCustomerId] = useState('');
  const [mAidNo, setMAidNo] = useState('');
  const [mAidSe, setMAidSe] = useState('');
  const [mAidFi, setMAidFi] = useState('');
  const [mUetNo, setMUetNo] = useState('');

  // Editable guardrail inputs
  const [budgetCapInput, setBudgetCapInput] = useState<number>(3500);
  const [targetRoasInput, setTargetRoasInput] = useState<number>(1.5);
  const [maxStepPctInput, setMaxStepPctInput] = useState<number>(15);
  const [modeInput, setModeInput] = useState<OptimizerMode>('recommend_only');

  // Proposal filters
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('pending');

  const loadDashboard = useCallback(async (targetGeo: OptimizerGeo, days: number) => {
    setLoading(true);
    setBanner(null);
    try {
      const [resp, connResp] = await Promise.all([
        fetchGeoOptimizerDashboard(targetGeo, days),
        fetchConnectionsStatus().catch(() => null),
      ]);
      setData(resp);
      if (connResp?.connections) {
        setConnections(connResp.connections);
        const g = connResp.connections.google;
        if (g) {
          setGMccId(g.login_customer_id || '');
          setGCidNo(g.customer_ids?.no || '');
          setGCidSe(g.customer_ids?.se || '');
          setGCidFi(g.customer_ids?.fi || '');
        }
        const m = connResp.connections.microsoft;
        if (m) {
          setMCustomerId(m.customer_id || '');
          setMAidNo(m.account_ids?.no || '');
          setMAidSe(m.account_ids?.se || '');
          setMAidFi(m.account_ids?.fi || '');
          setMUetNo(m.uet_tag_ids?.no || '');
        }
      }
      if (resp.settings) {
        setBudgetCapInput(resp.settings.daily_budget_cap_dkk);
        setTargetRoasInput(resp.settings.target_roas_d30);
        setMaxStepPctInput(Math.round((resp.settings.max_daily_step_pct || 0.15) * 100));
        setModeInput(resp.settings.mode || 'recommend_only');
      }
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || 'Failed to load GEO Profit Optimizer.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard(geo, windowDays);
  }, [geo, windowDays, loadDashboard]);

  const currentRedirectUri =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : 'https://afima.ai/simplyloan/optimizer';

  // Automatically handle OAuth 2.0 callback when Google or Microsoft redirects back to afima.ai
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state') || '';
    if (!code || (!state.includes('google') && !state.includes('microsoft'))) return;

    const platform: 'google' | 'microsoft' = state.includes('microsoft') ? 'microsoft' : 'google';
    const cleanUrl = `${window.location.origin}${window.location.pathname}`;
    window.history.replaceState({}, document.title, cleanUrl);

    (async () => {
      setSavingConn(true);
      try {
        const res = await exchangeOAuthCode({
          platform,
          code,
          redirect_uri: cleanUrl,
        });
        setConnections((prev) => ({ ...(prev || {}), [platform]: res.connections }));
        setBanner({
          type: 'success',
          text: `${
            platform === 'google' ? 'Google Ads' : 'Microsoft Advertising'
          } OAuth completed on ${cleanUrl}! Refresh token is now saved in AWS Secrets Manager.`,
        });
      } catch (err: any) {
        setBanner({
          type: 'error',
          text: `OAuth code exchange failed: ${err.message || 'Unknown error'}`,
        });
      } finally {
        setSavingConn(false);
      }
    })();
  }, []);

  const handleSaveAndResolve = async () => {
    setSavingSettings(true);
    setBanner(null);
    try {
      await updateGeoOptimizerSettings({
        geo,
        daily_budget_cap_dkk: Number(budgetCapInput),
        target_roas_d30: Number(targetRoasInput),
        max_daily_step_pct: Number(maxStepPctInput) / 100,
        mode: modeInput,
      });
      await triggerGeoOptimizerRun({
        geo,
        window_days: windowDays,
        daily_budget_cap_dkk: Number(budgetCapInput),
      });
      await loadDashboard(geo, windowDays);
      setBanner({
        type: 'success',
        text: `Updated ${geo.toUpperCase()} budget cap to ${budgetCapInput.toLocaleString()} DKK/day and re-solved cross-channel portfolio.`,
      });
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || 'Failed to update settings.' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    setBanner(null);
    try {
      const res = await triggerGeoOptimizerRun({
        geo,
        window_days: windowDays,
        daily_budget_cap_dkk: Number(budgetCapInput),
      });
      await loadDashboard(geo, windowDays);
      setBanner({
        type: 'success',
        text: res.summary || `Completed optimization pass for ${geo.toUpperCase()}.`,
      });
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || 'Failed to run optimizer.' });
    } finally {
      setRunning(false);
    }
  };

  const handleRunTier = async (tier: OptimizerTier) => {
    setTierRunning(tier);
    setBanner(null);
    try {
      const res = await runOptimizerTier(tier, geo, tier === 'intraday_guardian' ? { sync: false } : {});
      await loadDashboard(geo, windowDays);
      setBanner({
        type: 'success',
        text: `${TIER_META[tier].label}: ${res.summary || 'completed'} (${res.auto_applied_count || 0} applied, ${res.blocked_count || 0} blocked by the invariant gate).`,
      });
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || `Failed to run ${TIER_META[tier].label}.` });
    } finally {
      setTierRunning(null);
    }
  };

  const handleApplyProposal = async (proposal: OptimizerProposal) => {
    setBusyProposalId(proposal.id);
    setBanner(null);
    try {
      const res = await applyOptimizerProposal(proposal.id);
      if (res.proposal_status === 'applied') {
        setBanner({ type: 'success', text: `Applied: ${proposal.title}` });
      } else {
        setBanner({ type: 'error', text: `Execution returned status: ${res.status}` });
      }
      await loadDashboard(geo, windowDays);
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || 'Failed to apply proposal.' });
    } finally {
      setBusyProposalId(null);
    }
  };

  const handleRejectProposal = async (proposal: OptimizerProposal) => {
    setBusyProposalId(proposal.id);
    setBanner(null);
    try {
      await rejectOptimizerProposal(proposal.id);
      setBanner({ type: 'success', text: `Dismissed proposal: ${proposal.title}` });
      await loadDashboard(geo, windowDays);
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || 'Failed to dismiss proposal.' });
    } finally {
      setBusyProposalId(null);
    }
  };

  const handleRollbackProposal = async (proposal: OptimizerProposal) => {
    setBusyProposalId(proposal.id);
    setBanner(null);
    try {
      await rollbackOptimizerProposal(proposal.id);
      setBanner({ type: 'success', text: `Rolled back proposal: ${proposal.title}` });
      await loadDashboard(geo, windowDays);
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || 'Failed to roll back proposal.' });
    } finally {
      setBusyProposalId(null);
    }
  };

  const handleApplyAllSafe = async (includeBudgets: boolean) => {
    setRunning(true);
    setBanner(null);
    try {
      const res = await applyAllSafeOptimizerProposals(geo, includeBudgets);
      await loadDashboard(geo, windowDays);
      setBanner({
        type: 'success',
        text: `Applied ${res.applied_count} proposal(s) across ${geo.toUpperCase()} (${includeBudgets ? 'Safe Hygiene + Bounded Budget Shifts' : 'Safe Search Hygiene'}).`,
      });
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || 'Batch execution failed.' });
    } finally {
      setRunning(false);
    }
  };

  const handleSaveConnection = async (platform: 'google' | 'microsoft') => {
    setSavingConn(true);
    setBanner(null);
    try {
      const payload =
        platform === 'google'
          ? {
              platform: 'google',
              developer_token: gDevToken,
              client_id: gClientId,
              client_secret: gClientSecret,
              refresh_token: gRefreshToken,
              login_customer_id: gMccId,
              customer_ids: { no: gCidNo, se: gCidSe, fi: gCidFi },
            }
          : {
              platform: 'microsoft',
              developer_token: mDevToken,
              client_id: mClientId,
              client_secret: mClientSecret,
              refresh_token: mRefreshToken,
              customer_id: mCustomerId,
              account_ids: { no: mAidNo, se: mAidSe, fi: mAidFi },
              uet_tag_ids: { no: mUetNo },
            };

      const res = await savePlatformConnection(payload);
      setConnections((prev) => ({ ...(prev || {}), [platform]: res.connections }));
      setBanner({
        type: 'success',
        text: `Saved ${platform === 'google' ? 'Google Ads (prod/google_ads/app)' : 'Microsoft Advertising (prod/microsoft_ads/app)'} credentials to AWS Secrets Manager.`,
      });
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || `Failed to save ${platform} credentials.` });
    } finally {
      setSavingConn(false);
    }
  };

  const handleStartWebOAuth = async (platform: 'google' | 'microsoft') => {
    setSavingConn(true);
    setBanner(null);
    try {
      // Save any entered credentials (client_id, client_secret, dev token, customer/account IDs) first
      await handleSaveConnection(platform);
      const clientId = platform === 'google' ? gClientId : mClientId;
      const res = await fetchOAuthAuthorizeUrl({
        platform,
        redirect_uri: currentRedirectUri,
        client_id: clientId || undefined,
      });
      if (res.authorize_url) {
        window.location.href = res.authorize_url;
      }
    } catch (err: any) {
      setBanner({
        type: 'error',
        text: err.message || `Failed to start ${platform} browser OAuth.`,
      });
      setSavingConn(false);
    }
  };

  const handleTestConnection = async (platform: 'google' | 'microsoft') => {
    setTestingConn(true);
    setBanner(null);
    try {
      const res = await testPlatformConnection(platform, geo);
      if (res.status === 'ok') {
        setBanner({
          type: 'success',
          text:
            platform === 'google'
              ? `Google Ads API Connected: ${res.descriptive_name || res.customer_id} (${geo.toUpperCase()})`
              : `Microsoft Advertising API Connected: Account ${res.account_id} (${geo.toUpperCase()})`,
        });
      } else {
        setBanner({ type: 'error', text: res.reason || 'Connection test failed.' });
      }
    } catch (err: any) {
      setBanner({ type: 'error', text: err.message || 'API connection test failed.' });
    } finally {
      setTestingConn(false);
    }
  };

  const portfolio = data?.portfolio;
  const profile = data?.profile;
  const proposals = data?.proposals || [];

  const filteredProposals = proposals.filter((p) => {
    if (typeFilter !== 'all' && p.proposal_type !== typeFilter) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    return true;
  });

  const pendingCount = proposals.filter((p) => p.status === 'pending').length;
  const pendingSafeCount = proposals.filter((p) => p.status === 'pending' && p.is_safe_hygiene).length;

  return (
    <div className="min-h-screen p-6 space-y-6" style={{ backgroundColor: MINT }}>
      {/* Top Header Bar */}
      <div
        className="rounded-xl p-6 text-white shadow-md flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
        style={{ backgroundColor: PINE }}
      >
        <div>
          <div className="flex items-center gap-2.5">
            <span
              className="px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider"
              style={{ backgroundColor: ORANGE, color: '#fff' }}
            >
              AI Trading Desk
            </span>
            <span className="text-xs text-emerald-200 font-medium">
              Cross-Channel Cohort LTV Profit Maximizer • Google + Microsoft + Meta
            </span>
          </div>
          <h1 className="text-2xl font-bold mt-2 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-300" />
            SimplyLoan GEO Profit Optimizer — {profile?.country_name || 'Norway'} ({geo.toUpperCase()})
          </h1>
          <p className="text-sm text-emerald-100 mt-1">
            Domain: <span className="font-semibold text-white">{profile?.domain || 'simplyloan.no'}</span> • Legal
            Guardrail: <span className="font-semibold text-white">{profile?.legal_regime}</span> • Registry:{' '}
            <span className="font-semibold text-white">{profile?.credit_registry}</span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* GEO Switcher (Norway Primary) */}
          <div className="flex items-center bg-black/25 rounded-lg p-1 border border-white/15">
            {(['no', 'se', 'fi'] as OptimizerGeo[]).map((g) => (
              <button
                key={g}
                onClick={() => setGeo(g)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase transition ${
                  geo === g ? 'bg-white text-slate-900 shadow' : 'text-emerald-100 hover:bg-white/10'
                }`}
              >
                {g === 'no' ? '🇳🇴 NO (Primary)' : g === 'se' ? '🇸🇪 SE' : '🇫🇮 FI'}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowConnectModal(true)}
            className="px-3.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-white/15 hover:bg-white/25 text-white border border-white/20 flex items-center gap-1.5 transition"
          >
            <KeyRound className="w-3.5 h-3.5" />
            Connect Ad Accounts
          </button>

          <button
            onClick={handleRunNow}
            disabled={running || loading}
            className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 shadow transition hover:opacity-95 disabled:opacity-50"
            style={{ backgroundColor: ORANGE, color: '#fff' }}
          >
            <Play className="w-3.5 h-3.5" />
            {running ? 'Solving...' : 'Run Daily Optimizer'}
          </button>

          {pendingSafeCount > 0 && (
            <button
              onClick={() => handleApplyAllSafe(false)}
              disabled={running}
              className="px-3.5 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1.5 shadow transition"
            >
              <Zap className="w-3.5 h-3.5" />
              Apply Safe Hygiene ({pendingSafeCount})
            </button>
          )}
        </div>
      </div>

      {/* Monthly Autonomous P&L Command Center (60K budget / 20K max loss / 400-2,500 DKK per day) */}
      {(() => {
        const pnl = data?.pnl || null;
        const health = data?.health || null;
        const funnel = data?.funnel || null;
        const autonomy = data?.autonomy;
        const regime = REGIME_META[pnl?.pacing_regime || ''] || REGIME_META.pro_rata_pace;
        const budgetCap = pnl?.monthly_budget_cap_dkk || 60000;
        const lossCap = pnl?.monthly_max_loss_dkk || 20000;
        const spendPct = pnl ? Math.min(100, Math.max(0, (pnl.mtd_spend_total_dkk / budgetCap) * 100)) : 0;
        const projectedPct = pnl ? Math.min(100, Math.max(0, ((pnl.projected_eom_spend_dkk || 0) / budgetCap) * 100)) : 0;
        const lossPct = pnl ? Math.min(100, Math.max(0, (pnl.mtd_net_loss_dkk / lossCap) * 100)) : 0;
        const lossTone = lossPct < 60 ? 'emerald' : lossPct < 85 ? 'amber' : 'red';
        const breakdown = pnl?.revenue_breakdown;
        const alphas = (pnl?.advertiser_alphas || []).slice(0, 5);
        const lenders = (funnel?.lenders_30d?.length ? funnel.lenders_30d : funnel?.lenders_90d || []).slice(0, 6);
        const routes = funnel?.routing?.clusters || [];
        const alerts = [...(pnl?.alerts || []), ...(health?.alerts || [])];
        const isAutopilot = data?.settings?.mode === 'full_autopilot';
        return (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3" style={{ backgroundColor: '#F4F8F6' }}>
              <div>
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Bot className="w-4 h-4 text-emerald-800" />
                  Monthly Autonomous P&amp;L Command Center — {profile?.domain || 'simplyloan.no'} ({pnl?.month_key || '—'})
                </h2>
                <p className="text-xs text-slate-500">
                  Hard invariants: {dkk(budgetCap)} DKK monthly ceiling • {dkk(lossCap)} DKK max net loss (always wins) •{' '}
                  {dkk(pnl?.min_daily_floor_dkk || 400)}–{dkk(pnl?.max_daily_cap_dkk || 2500)} DKK/day anti-turtle corridor • Europe/Copenhagen calendar month
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${regime.color}`}>{regime.label}</span>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    isAutopilot ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-700 border-slate-200'
                  }`}
                >
                  {isAutopilot ? 'Full Autopilot ON' : `Mode: ${data?.settings?.mode || 'recommend_only'}`}
                </span>
                {health?.freeze && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-600 text-white flex items-center gap-1">
                    <ShieldAlert className="w-3.5 h-3.5" /> Tracking / Sync Freeze
                  </span>
                )}
              </div>
            </div>

            {alerts.length > 0 && (
              <div className="px-5 py-2 border-b border-slate-200 space-y-1">
                {alerts.map((a, idx) => (
                  <div
                    key={`${a.code}-${idx}`}
                    className={`text-xs font-medium flex items-start gap-2 ${a.level === 'critical' ? 'text-rose-800' : 'text-amber-800'}`}
                  >
                    <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      <strong className="uppercase">{a.level}</strong> · {a.message}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 p-5">
              {/* Monthly Budget Utilization Gauge */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5">
                  <Gauge className="w-3.5 h-3.5 text-emerald-800" /> Monthly Budget Utilization
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-extrabold text-slate-900">{dkk(pnl?.mtd_spend_total_dkk)} DKK</span>
                  <span className="text-xs text-slate-500">/ {dkk(budgetCap)} DKK MTD</span>
                </div>
                <div className="relative h-3 rounded-full bg-slate-100 overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-slate-300" style={{ width: `${projectedPct}%` }} />
                  <div className="absolute inset-y-0 left-0" style={{ width: `${spendPct}%`, backgroundColor: PINE }} />
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Projected end of month</span>
                    <span className="font-semibold text-slate-900">{dkk(pnl?.projected_eom_spend_dkk)} DKK</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Today&rsquo;s solved daily cap (B_today)</span>
                    <span className="font-bold" style={{ color: ORANGE }}>
                      {dkk(pnl?.today_allowed_daily_budget_dkk)} DKK/day
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Remaining budget · days left</span>
                    <span className="font-semibold text-slate-900">
                      {dkk(pnl?.remaining_budget_dkk)} DKK · {pnl?.days_remaining ?? '—'} d
                    </span>
                  </div>
                  <div className="flex justify-between text-[11px] text-slate-500">
                    <span>Google {dkk(pnl?.mtd_spend_google_dkk)} · Meta {dkk(pnl?.mtd_spend_meta_dkk)} · Microsoft {dkk(pnl?.mtd_spend_microsoft_dkk)}</span>
                  </div>
                </div>
              </div>

              {/* Monthly Net Loss / Drawdown Guardrail Bar */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-800" /> Net Loss Guardrail
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl font-extrabold ${lossTone === 'emerald' ? 'text-emerald-700' : lossTone === 'amber' ? 'text-amber-700' : 'text-rose-700'}`}>
                    {dkk(pnl?.mtd_net_loss_dkk)} DKK
                  </span>
                  <span className="text-xs text-slate-500">/ {dkk(lossCap)} DKK max loss</span>
                </div>
                <div className="relative h-3 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 ${lossTone === 'emerald' ? 'bg-emerald-500' : lossTone === 'amber' ? 'bg-amber-500' : 'bg-rose-600'}`}
                    style={{ width: `${lossPct}%` }}
                  />
                  <div className="absolute inset-y-0 border-l-2 border-rose-400" style={{ left: '75%' }} title="Critical drawdown zone (15,000 DKK)" />
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Remaining loss buffer</span>
                    <span className="font-semibold text-slate-900">{dkk(pnl?.remaining_loss_buffer_dkk)} DKK</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Projected end-of-month net loss</span>
                    <span className="font-semibold text-slate-900">{dkk(pnl?.projected_eom_net_loss_dkk)} DKK</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bayesian 14d site ROAS (ρ̂)</span>
                    <span className="font-semibold text-slate-900">{(pnl?.trailing_roas_14d ?? 0).toFixed(2)}x</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Pacing regime</span>
                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold border ${regime.color}`}>{regime.label}</span>
                  </div>
                </div>
              </div>

              {/* SimplyLoan.no Total Revenue Breakdown */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5">
                  <Coins className="w-3.5 h-3.5 text-emerald-800" /> {profile?.domain || 'simplyloan.no'} Total Revenue (MTD)
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-extrabold text-emerald-700">{dkk(pnl?.mtd_rev_total_risk_adj_dkk)} DKK</span>
                  <span className="text-xs text-slate-500">risk-adjusted</span>
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Approved cash revenue</span>
                    <span className="font-semibold text-slate-900">{dkk(pnl?.mtd_rev_approved_dkk)} DKK</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      Pending × α (raw {dkk(pnl?.mtd_rev_pending_raw_dkk)})
                    </span>
                    <span className="font-semibold text-slate-900">{dkk(pnl?.mtd_rev_pending_risk_adj_dkk)} DKK</span>
                  </div>
                  <div className="pt-1 border-t border-slate-100 flex justify-between">
                    <span>Paid D0 (slnoga / slnome / slnoma)</span>
                    <span className="font-mono">{dkk(breakdown?.paid_d0_revenue_dkk)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Email drip (slnoem / Brevo)</span>
                    <span className="font-mono">{dkk(breakdown?.email_drip_revenue_dkk)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Organic / SEO (slnoww)</span>
                    <span className="font-mono">{dkk(breakdown?.organic_seo_revenue_dkk)}</span>
                  </div>
                  {alphas.length > 0 && (
                    <div className="pt-1 border-t border-slate-100">
                      <div className="text-[11px] uppercase font-semibold text-slate-400 mb-1">Lender approval α (90d)</div>
                      {alphas.map((a) => (
                        <div key={a.advertiser} className="flex justify-between text-[11px]">
                          <span className="truncate pr-2">
                            {a.advertiser} <span className="text-slate-400">({a.approved_90d}/{a.approved_90d + a.declined_90d}{a.shrunk ? ', shrunk' : ''})</span>
                          </span>
                          <span className="font-mono font-semibold">{pct(a.alpha, 0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Full Autopilot Mode & 3-Tier Loop Triggers */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5 text-emerald-800" /> 3-Tier Autonomous Loop
                </div>
                <div className="space-y-2">
                  {(Object.keys(TIER_META) as OptimizerTier[]).map((tier) => {
                    const last = autonomy?.tiers?.[tier];
                    return (
                      <div key={tier} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-slate-900">
                            {TIER_META[tier].label} <span className="text-slate-400 font-normal">· {TIER_META[tier].cadence}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 truncate" title={last?.summary || TIER_META[tier].hint}>
                            {last ? `Last run ${new Date(last.created_at).toLocaleString()} · ${last.auto_applied_count ?? 0} applied` : TIER_META[tier].hint}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRunTier(tier)}
                          disabled={tierRunning !== null || loading}
                          className="shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-white shadow transition hover:opacity-95 disabled:opacity-50"
                          style={{ backgroundColor: tierRunning === tier ? ORANGE : PINE }}
                        >
                          {tierRunning === tier ? 'Running…' : 'Run now'}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[11px] text-slate-500">
                  Principal: <code>{autonomy?.principal || 'autonomous_marketing_agent'}</code>. Every live write passes the 6-check pre-mutation gate
                  (compliance, B_today, 60K, 20K, anti-turtle, tracking freeze).
                </div>
              </div>
            </div>

            {/* Lender EPC & Funnel Routing Panel */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 px-5 pb-5">
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-slate-200 text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5">
                  <Coins className="w-3.5 h-3.5 text-emerald-800" /> Live Norwegian Lender EPC &amp; Approval (30d)
                </div>
                {lenders.length === 0 ? (
                  <div className="px-4 py-6 text-xs text-slate-500">No /slgo/ partner clicks recorded yet for this market.</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-2">Partner</th>
                        <th className="text-right px-2 py-2">Clicks</th>
                        <th className="text-right px-2 py-2">EPC</th>
                        <th className="text-right px-2 py-2">CR</th>
                        <th className="text-right px-4 py-2">α</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lenders.map((l) => (
                        <tr key={l.slug}>
                          <td className="px-4 py-1.5 font-semibold text-slate-800">
                            {l.slug}
                            {l.advertiser && <span className="text-slate-400 font-normal"> · {l.advertiser}</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">{l.clicks}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-800">{dkk(l.epc_dkk)} DKK</td>
                          <td className="px-2 py-1.5 text-right font-mono">{pct(l.conversion_rate)}</td>
                          <td className="px-4 py-1.5 text-right font-mono">
                            {pct(l.approval_rate_alpha, 0)}
                            {l.alpha_shrunk && <span className="text-slate-400"> *</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {funnel?.calibration && (
                  <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500">
                    Smart-bidding values: Lead G {dkk(funnel.calibration.lead_value_dkk?.google)} · M {dkk(funnel.calibration.lead_value_dkk?.meta)} · MS{' '}
                    {dkk(funnel.calibration.lead_value_dkk?.microsoft)} DKK · click-out {dkk(funnel.calibration.clickout_epc_dkk?.['*'])} DKK avg
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-slate-200 text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5">
                  <Route className="w-3.5 h-3.5 text-emerald-800" /> Landing Page Routing (Bayesian D30 RPL)
                </div>
                {routes.length === 0 ? (
                  <div className="px-4 py-6 text-xs text-slate-500">Routing weights appear after the first daily solver run.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {routes.map((r) => (
                      <div key={r.cluster} className="px-4 py-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-800">{r.cluster}</span>
                          <span className="font-mono text-emerald-800 font-bold">
                            {r.chosen_path} · {dkk(r.chosen_rpl_d30_dkk)} DKK
                            {!r.reroute_allowed && <span className="text-slate-400 font-normal"> (prior)</span>}
                          </span>
                        </div>
                        <div className="mt-1 flex gap-1">
                          {r.candidates.map((c) => (
                            <div
                              key={c.path}
                              className="h-2 rounded-full"
                              title={`${c.path}: ${dkk(c.bayesian_rpl_d30_dkk)} DKK RPL, ${c.leads} leads`}
                              style={{ width: `${Math.max(4, c.weight * 100)}%`, backgroundColor: c.path === r.chosen_path ? ORANGE : '#DCEBE5' }}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {health && (
                  <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500 flex flex-wrap gap-x-3">
                    <span>Click-outs 12h: <strong>{health.clicks_12h ?? '—'}</strong></span>
                    <span>prior 7d: <strong>{health.clicks_prior_7d ?? '—'}</strong></span>
                    <span>Stale syncs: <strong>{health.stale_platforms?.length ? health.stale_platforms.join(', ') : 'none'}</strong></span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Quick Platform Connection & Navigation Strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl px-4 py-3 border border-slate-200 shadow-sm text-xs">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-semibold text-slate-700 flex items-center gap-1.5">
            <PlugZap className="w-4 h-4 text-emerald-700" />
            Ad Platform Connections:
          </span>
          {(['google', 'microsoft', 'meta'] as OptimizerPlatform[]).map((plat) => {
            const c = connections?.[plat];
            const isConn = c?.connected;
            return (
              <button
                key={plat}
                onClick={() => {
                  if (plat === 'google' || plat === 'microsoft') {
                    setConnectTab(plat);
                    setShowConnectModal(true);
                  }
                }}
                className={`px-2.5 py-1 rounded-full font-semibold border flex items-center gap-1.5 ${
                  isConn
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${isConn ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                <span className="capitalize">{plat} Ads:</span>
                <span>{isConn ? 'Connected' : 'Setup Credentials'}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3 font-medium">
          <Link to="/simplyloan/google" className="text-blue-700 hover:underline">
            Google Ads →
          </Link>
          <Link to="/simplyloan/microsoft" className="text-teal-700 hover:underline">
            Microsoft Ads →
          </Link>
          <Link to="/simplyloan/meta" className="text-indigo-700 hover:underline">
            Meta Ads →
          </Link>
        </div>
      </div>

      {/* Connect Google Ads & Microsoft Advertising Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-200 space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-emerald-800" />
                  Connect Google Ads & Microsoft Advertising
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Saves credentials directly to AWS Secrets Manager (<code>prod/google_ads/app</code> &amp;{' '}
                  <code>prod/microsoft_ads/app</code> in <code>eu-west-1</code>)
                </p>
              </div>
              <button
                onClick={() => setShowConnectModal(false)}
                className="text-xs font-semibold text-slate-500 hover:text-slate-800 px-2.5 py-1 rounded border border-slate-200"
              >
                Close
              </button>
            </div>

            <div className="flex gap-2 bg-slate-100 p-1 rounded-lg text-xs font-bold">
              <button
                onClick={() => setConnectTab('google')}
                className={`flex-1 py-2 rounded-md transition ${
                  connectTab === 'google' ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-600'
                }`}
              >
                1. Google Ads API (prod/google_ads/app)
              </button>
              <button
                onClick={() => setConnectTab('microsoft')}
                className={`flex-1 py-2 rounded-md transition ${
                  connectTab === 'microsoft' ? 'bg-white text-teal-900 shadow-sm' : 'text-slate-600'
                }`}
              >
                2. Microsoft Advertising API (prod/microsoft_ads/app)
              </button>
            </div>

            {connectTab === 'google' ? (
              <div className="space-y-3 text-xs">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-900 space-y-1">
                  <div className="font-bold">How to connect Google Ads on afima.ai:</div>
                  <div>
                    1. <strong>Developer Token</strong>: Google Ads Manager (MCC) → Tools &amp; Settings → Setup → API Center.
                  </div>
                  <div>
                    2. <strong>OAuth Client ID &amp; Secret</strong>: Google Cloud Console → Credentials → Web Application → Authorized Redirect URI:{' '}
                    <code className="bg-white px-1.5 py-0.5 rounded border border-blue-200 font-bold">
                      {currentRedirectUri}
                    </code>{' '}
                    (or <code>https://afima.ai/simplyloan/optimizer</code> in production).
                  </div>
                  <div>
                    3. <strong>Authorize</strong>: Click <strong>Save &amp; Authorize in Browser</strong> below to sign in directly on{' '}
                    <code>{currentRedirectUri}</code>, or paste an existing Refresh Token.
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Developer Token</label>
                    <input
                      type="password"
                      placeholder={connections?.google?.developer_token_masked || 'Paste Developer Token'}
                      value={gDevToken}
                      onChange={(e) => setGDevToken(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">MCC Login Customer ID (10 digits)</label>
                    <input
                      type="text"
                      placeholder="e.g. 1234567890"
                      value={gMccId}
                      onChange={(e) => setGMccId(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">OAuth Client ID</label>
                    <input
                      type="text"
                      placeholder={connections?.google?.client_id_masked || 'xxx.apps.googleusercontent.com'}
                      value={gClientId}
                      onChange={(e) => setGClientId(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">OAuth Client Secret</label>
                    <input
                      type="password"
                      placeholder="GOCSPX-..."
                      value={gClientSecret}
                      onChange={(e) => setGClientSecret(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-semibold text-slate-700 mb-1">
                      OAuth Refresh Token (auto-filled if you click &ldquo;Save &amp; Authorize in Browser&rdquo;)
                    </label>
                    <input
                      type="password"
                      placeholder={connections?.google?.has_refresh_token ? '•••••••• (Saved in Secrets Manager)' : '1//0g...'}
                      value={gRefreshToken}
                      onChange={(e) => setGRefreshToken(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">🇳🇴 Norway Customer ID (no)</label>
                    <input
                      type="text"
                      placeholder="10-digit Customer ID"
                      value={gCidNo}
                      onChange={(e) => setGCidNo(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">🇸🇪 Sweden Customer ID (se)</label>
                    <input
                      type="text"
                      placeholder="10-digit Customer ID"
                      value={gCidSe}
                      onChange={(e) => setGCidSe(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">🇫🇮 Finland Customer ID (fi)</label>
                    <input
                      type="text"
                      placeholder="10-digit Customer ID"
                      value={gCidFi}
                      onChange={(e) => setGCidFi(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 pt-3 border-t border-slate-200">
                  <button
                    onClick={() => handleTestConnection('google')}
                    disabled={testingConn}
                    className="px-3.5 py-2 rounded-lg font-bold border border-blue-300 text-blue-800 bg-blue-50 hover:bg-blue-100"
                  >
                    {testingConn ? 'Testing...' : 'Test Handshake'}
                  </button>
                  <button
                    onClick={() => handleSaveConnection('google')}
                    disabled={savingConn}
                    className="px-3.5 py-2 rounded-lg font-bold border border-slate-300 text-slate-800 bg-white hover:bg-slate-50"
                  >
                    {savingConn ? 'Saving...' : 'Save Credentials'}
                  </button>
                  <button
                    onClick={() => handleStartWebOAuth('google')}
                    disabled={savingConn}
                    className="px-4 py-2 rounded-lg font-bold text-white shadow"
                    style={{ backgroundColor: ORANGE }}
                  >
                    Save &amp; Authorize in Browser →
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-xs">
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-teal-900 space-y-1">
                  <div className="font-bold">How to connect Microsoft Advertising on afima.ai:</div>
                  <div>
                    1. <strong>Developer Token</strong>: Sign in at <code>developers.ads.microsoft.com/Account</code>.
                  </div>
                  <div>
                    2. <strong>Azure App Client ID</strong>: Azure Portal → App Registrations → Web Redirect URI:{' '}
                    <code className="bg-white px-1.5 py-0.5 rounded border border-teal-200 font-bold">
                      {currentRedirectUri}
                    </code>{' '}
                    (or <code>https://afima.ai/simplyloan/optimizer</code> in production).
                  </div>
                  <div>
                    3. <strong>Authorize</strong>: Click <strong>Save &amp; Authorize in Browser</strong> below to sign in directly on{' '}
                    <code>{currentRedirectUri}</code>, or paste an existing Refresh Token.
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Developer Token</label>
                    <input
                      type="password"
                      placeholder={connections?.microsoft?.developer_token_masked || 'Paste Developer Token'}
                      value={mDevToken}
                      onChange={(e) => setMDevToken(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Parent Customer ID (numeric)</label>
                    <input
                      type="text"
                      placeholder="e.g. 50123456"
                      value={mCustomerId}
                      onChange={(e) => setMCustomerId(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Azure Client ID (Application ID)</label>
                    <input
                      type="text"
                      placeholder={connections?.microsoft?.client_id_masked || 'UUID Client ID'}
                      value={mClientId}
                      onChange={(e) => setMClientId(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Azure Client Secret (for Web Redirect)</label>
                    <input
                      type="password"
                      placeholder="Client secret from Azure Certificates & secrets"
                      value={mClientSecret}
                      onChange={(e) => setMClientSecret(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-semibold text-slate-700 mb-1">
                      OAuth Refresh Token (auto-filled if you click &ldquo;Save &amp; Authorize in Browser&rdquo;)
                    </label>
                    <input
                      type="password"
                      placeholder={connections?.microsoft?.has_refresh_token ? '•••••••• (Saved in Secrets Manager)' : 'M.C5...'}
                      value={mRefreshToken}
                      onChange={(e) => setMRefreshToken(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">🇳🇴 Norway Account ID (no)</label>
                    <input
                      type="text"
                      placeholder="Numeric Account ID"
                      value={mAidNo}
                      onChange={(e) => setMAidNo(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">🇳🇴 Norway UET Tag ID (no)</label>
                    <input
                      type="text"
                      placeholder="Numeric UET Tag ID"
                      value={mUetNo}
                      onChange={(e) => setMUetNo(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">🇸🇪 Sweden Account ID (se)</label>
                    <input
                      type="text"
                      placeholder="Numeric Account ID"
                      value={mAidSe}
                      onChange={(e) => setMAidSe(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">🇫🇮 Finland Account ID (fi)</label>
                    <input
                      type="text"
                      placeholder="Numeric Account ID"
                      value={mAidFi}
                      onChange={(e) => setMAidFi(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 pt-3 border-t border-slate-200">
                  <button
                    onClick={() => handleTestConnection('microsoft')}
                    disabled={testingConn}
                    className="px-3.5 py-2 rounded-lg font-bold border border-teal-300 text-teal-800 bg-teal-50 hover:bg-teal-100"
                  >
                    {testingConn ? 'Testing...' : 'Test Handshake'}
                  </button>
                  <button
                    onClick={() => handleSaveConnection('microsoft')}
                    disabled={savingConn}
                    className="px-3.5 py-2 rounded-lg font-bold border border-slate-300 text-slate-800 bg-white hover:bg-slate-50"
                  >
                    {savingConn ? 'Saving...' : 'Save Credentials'}
                  </button>
                  <button
                    onClick={() => handleStartWebOAuth('microsoft')}
                    disabled={savingConn}
                    className="px-4 py-2 rounded-lg font-bold text-white shadow"
                    style={{ backgroundColor: ORANGE }}
                  >
                    Save &amp; Authorize in Browser →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {banner && (
        <div
          className={`rounded-xl px-4 py-3 text-sm font-medium flex items-center justify-between border ${
            banner.type === 'success'
              ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
              : 'bg-rose-50 text-rose-900 border-rose-200'
          }`}
        >
          <span>{banner.text}</span>
          <button onClick={() => setBanner(null)} className="text-xs underline ml-4">
            Dismiss
          </button>
        </div>
      )}

      {/* Budget Cap & Guardrails Control Bar */}
      <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
            <Sliders className="w-4 h-4 text-emerald-800" />
            {geo.toUpperCase()} Portfolio Constraints & Smart-Bidding Guardrails
          </h2>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Cohort Lookback Window:</span>
            {[14, 30, 60].map((d) => (
              <button
                key={d}
                onClick={() => setWindowDays(d)}
                className={`px-2.5 py-1 rounded font-semibold ${
                  windowDays === d ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Target Daily Budget Cap (DKK)
            </label>
            <input
              type="number"
              min={300}
              step={100}
              value={budgetCapInput}
              onChange={(e) => setBudgetCapInput(Number(e.target.value))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Target D30 ROAS Hurdle (x)
            </label>
            <input
              type="number"
              min={0.8}
              max={5.0}
              step={0.05}
              value={targetRoasInput}
              onChange={(e) => setTargetRoasInput(Number(e.target.value))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Max Daily Step Clamp (±%)
            </label>
            <input
              type="number"
              min={5}
              max={30}
              step={1}
              value={maxStepPctInput}
              onChange={(e) => setMaxStepPctInput(Number(e.target.value))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Agent Autonomy Mode
            </label>
            <select
              value={modeInput}
              onChange={(e) => setModeInput(e.target.value as OptimizerMode)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600"
            >
              <option value="recommend_only">Recommend Only (1-Click Approve)</option>
              <option value="auto_hygiene">Auto-Hygiene (Auto Negatives & Exact Promos)</option>
              <option value="autopilot_bounded">Bounded Autopilot (±15% Budgets + Hygiene)</option>
              <option value="full_autopilot">Full End-to-End Autopilot (60K Budget / 20K Max Loss)</option>
            </select>
          </div>

          <div>
            <button
              onClick={handleSaveAndResolve}
              disabled={savingSettings}
              className="w-full py-2.5 px-4 rounded-lg text-xs font-bold uppercase tracking-wider text-white shadow transition hover:opacity-95 disabled:opacity-50"
              style={{ backgroundColor: PINE }}
            >
              {savingSettings ? 'Re-Solving...' : 'Save & Re-Solve Portfolio'}
            </button>
          </div>
        </div>
      </div>

      {/* Portfolio KPI Cards */}
      {portfolio && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="text-xs font-semibold uppercase text-slate-500">Daily Spend vs Cap</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold text-slate-900">
                {(portfolio.recommended_spend_daily_dkk ?? 0).toLocaleString()} DKK
              </span>
              <span className="text-xs text-slate-500">
                / {(portfolio.budget_cap_dkk ?? 0).toLocaleString()} DKK cap
              </span>
            </div>
            <div className="mt-1 text-xs text-slate-600">
              Current baseline: <span className="font-semibold">{(portfolio.current_spend_daily_dkk ?? 0).toLocaleString()} DKK/day</span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="text-xs font-semibold uppercase text-slate-500">Projected D30 Cohort ROAS</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold text-emerald-700">
                {(portfolio.optimized_projected_d30_roas ?? 0).toFixed(2)}x
              </span>
              <span className="text-xs font-semibold text-emerald-600 flex items-center">
                <ArrowUpRight className="w-3.5 h-3.5" />
                from {(portfolio.current_projected_d30_roas ?? 0).toFixed(2)}x
              </span>
            </div>
            <div className="mt-1 text-xs text-slate-600">
              Empirical Bayes smoothed across 30d cohorts
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="text-xs font-semibold uppercase text-slate-500">Expected Daily Net Profit Lift</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold" style={{ color: ORANGE }}>
                +{(portfolio.expected_daily_margin_lift_dkk ?? 0).toLocaleString()} DKK/day
              </span>
            </div>
            <div className="mt-1 text-xs text-slate-600">
              ≈ +{((portfolio.expected_daily_margin_lift_dkk ?? 0) * 30).toLocaleString()} DKK / 30 days
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="text-xs font-semibold uppercase text-slate-500">Channel Split (Recommended)</div>
            <div className="mt-2 space-y-1 text-xs">
              {(['google', 'microsoft', 'meta'] as OptimizerPlatform[]).map((plat) => {
                const ch = portfolio.channel_totals?.[plat];
                if (!ch) return null;
                const diff = Math.round((ch.recommended_daily_dkk ?? 0) - (ch.current_daily_dkk ?? 0));
                return (
                  <div key={plat} className="flex items-center justify-between">
                    <span className="font-semibold text-slate-700 capitalize">{plat}:</span>
                    <span className="font-mono font-bold text-slate-900">
                      {Math.round(ch.recommended_daily_dkk ?? 0)} DKK/d{' '}
                      <span className={diff >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                        ({diff >= 0 ? `+${diff}` : diff})
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Cross-Channel Campaign Portfolio Allocation Table */}
      {portfolio && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Layers className="w-4 h-4 text-emerald-800" />
                Cross-Channel Campaign Allocation Matrix ({geo.toUpperCase()})
              </h2>
              <p className="text-xs text-slate-500">
                Empirical Bayes-smoothed D30 Revenue Per Lead (RPL) vs. CPL and Search Impression Share headroom
              </p>
            </div>
            {(portfolio.campaigns || []).some((c) => c.is_reference_data) && (
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
                Reference Norway Portfolio Active (Sync live insights to replace)
              </span>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 uppercase tracking-wider font-semibold">
                  <th className="py-3 px-4">Channel</th>
                  <th className="py-3 px-4">Campaign</th>
                  <th className="py-3 px-3 text-right">30d Spend</th>
                  <th className="py-3 px-3 text-right">30d Leads</th>
                  <th className="py-3 px-3 text-right">Bayes RPL (D30)</th>
                  <th className="py-3 px-3 text-right">Bayes CPL</th>
                  <th className="py-3 px-3 text-right">Bayes D30 ROAS</th>
                  <th className="py-3 px-4">Headroom / Saturation</th>
                  <th className="py-3 px-3 text-right">Current Budget</th>
                  <th className="py-3 px-3 text-right">Optimal Budget</th>
                  <th className="py-3 px-3 text-right">Step Δ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(portfolio.campaigns || []).map((c) => {
                  const pMeta = PLATFORM_META[c.platform] || PLATFORM_META.google;
                  const deltaDkk = c.budget_delta_dkk ?? 0;
                  const deltaPct = c.budget_delta_pct ?? 0;
                  const bayesRoas = c.bayesian_roas_d30 ?? 0;
                  const currB = c.current_daily_budget_dkk ?? 0;
                  const recB = c.recommended_daily_budget_dkk ?? currB;
                  const isUp = deltaDkk > 0;
                  const isDown = deltaDkk < 0;
                  return (
                    <tr key={`${c.platform}-${c.campaign_id}`} className="hover:bg-slate-50/80">
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border ${pMeta.bg} ${pMeta.text} ${pMeta.border}`}
                        >
                          {pMeta.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-semibold text-slate-900">
                        <div>{c.campaign_name}</div>
                        <div className="text-[11px] text-slate-400 font-normal">
                          ID: {c.campaign_id} • {c.bidding_strategy}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-slate-700">
                        {Math.round(c.spend_30d_dkk ?? 0).toLocaleString()} DKK
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-semibold text-slate-800">
                        {c.leads_30d ?? 0}
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-emerald-800 font-semibold">
                        {Math.round(c.bayesian_rpl_d30_dkk ?? 0)} DKK
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-slate-700">
                        {Math.round(c.bayesian_cpl_dkk ?? 0)} DKK
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold">
                        <span
                          className={`px-2 py-0.5 rounded ${
                            bayesRoas >= 1.45
                              ? 'bg-emerald-50 text-emerald-800'
                              : bayesRoas >= 1.05
                              ? 'bg-amber-50 text-amber-800'
                              : 'bg-rose-50 text-rose-800'
                          }`}
                        >
                          {bayesRoas.toFixed(2)}x
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{c.headroom_label || '—'}</td>
                      <td className="py-3 px-3 text-right font-mono text-slate-700">
                        {Math.round(currB)} DKK/d
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-slate-900">
                        {Math.round(recB)} DKK/d
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold">
                        {isUp ? (
                          <span className="inline-flex items-center text-emerald-700">
                            <ArrowUpRight className="w-3.5 h-3.5 mr-0.5" />+{deltaPct.toFixed(1)}%
                          </span>
                        ) : isDown ? (
                          <span className="inline-flex items-center text-rose-700">
                            <ArrowDownRight className="w-3.5 h-3.5 mr-0.5" />
                            {deltaPct.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-slate-400">Hold</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Daily Action Queue (1-Click Approve / Reject / Rollback) */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-slate-200 pb-4">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-800" />
              Daily Action Queue — {pendingCount} Pending Recommendation(s)
            </h2>
            <p className="text-xs text-slate-500">
              Approve individual recommendations in 1 click, or roll back any previously applied change
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Category Filter */}
            <div className="flex items-center bg-slate-100 rounded-lg p-1 text-xs font-semibold">
              {[
                { key: 'all', label: 'All Types' },
                { key: 'budget_shift', label: 'Budgets' },
                { key: 'negative_keyword', label: 'Negatives' },
                { key: 'keyword_promotion', label: 'Promotions' },
                { key: 'new_rsa', label: 'RSAs' },
                { key: 'pause_bleeder', label: 'Bleeders' },
                { key: 'bid_update', label: 'Bids' },
                { key: 'campaign_create', label: 'Builds' },
                { key: 'meta_creative', label: 'Creatives' },
                { key: 'ad_pause', label: 'Fatigue' },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTypeFilter(t.key)}
                  className={`px-2.5 py-1 rounded-md transition ${
                    typeFilter === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending ({pendingCount})</option>
              <option value="applied">Applied</option>
              <option value="rolled_back">Rolled Back</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {filteredProposals.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">
            No proposals match the selected filter. Click <strong>Run Daily Optimizer</strong> to generate fresh recommendations.
          </div>
        ) : (
          <div className="space-y-3">
            {filteredProposals.map((p) => {
              const pMeta = PLATFORM_META[p.platform] || PLATFORM_META.google;
              const tMeta = PROPOSAL_TYPE_LABELS[p.proposal_type] || {
                label: p.proposal_type,
                color: 'bg-slate-100 text-slate-800 border-slate-200',
              };
              const isBusy = busyProposalId === p.id;
              const rsaSpec = p.action_payload?.rsa_spec;

              return (
                <div
                  key={p.id}
                  className="rounded-xl border border-slate-200 p-4 hover:border-slate-300 transition bg-slate-50/40 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
                >
                  <div className="space-y-2 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase ${
                          p.priority === 'critical'
                            ? 'bg-red-600 text-white'
                            : p.priority === 'high'
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-200 text-slate-800'
                        }`}
                      >
                        {p.priority}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded text-[11px] font-bold border ${pMeta.bg} ${pMeta.text} ${pMeta.border}`}
                      >
                        {pMeta.label}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${tMeta.color}`}>
                        {tMeta.label}
                      </span>
                      {p.is_safe_hygiene && (
                        <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-900">
                          Safe Hygiene
                        </span>
                      )}
                      <span className="text-xs text-slate-500">
                        Target: <strong className="text-slate-700">{p.target_object_name}</strong>
                      </span>
                      <span className="text-xs text-slate-400">
                        • Confidence: {Math.round((p.confidence || 0.8) * 100)}%
                      </span>
                    </div>

                    <div className="text-sm font-bold text-slate-900">{p.title}</div>
                    <p className="text-xs text-slate-600 leading-relaxed">{p.rationale}</p>

                    {/* If this is a Compliant RSA proposal, display headlines & descriptions preview */}
                    {rsaSpec && (
                      <div className="mt-2 bg-white rounded-lg p-3 border border-slate-200 text-xs space-y-1.5">
                        <div className="font-semibold text-slate-700 flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-purple-700" />
                          Pre-Validated RSA Assets ({p.action_payload?.legal_regime}):
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(rsaSpec.headlines || []).map((h: string, idx: number) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-purple-50 text-purple-900 border border-purple-200 rounded font-medium"
                            >
                              H{idx + 1}: {h} ({h.length}/30)
                            </span>
                          ))}
                        </div>
                        <div className="space-y-1 pt-1">
                          {(rsaSpec.descriptions || []).map((d: string, idx: number) => (
                            <div key={idx} className="text-slate-700 bg-slate-50 px-2 py-1 rounded border border-slate-200">
                              <strong>D{idx + 1}:</strong> {d} <span className="text-slate-400">({d.length}/90)</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Side: Margin Impact + 1-Click Controls */}
                  <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between gap-3 min-w-[210px]">
                    <div className="text-right">
                      <div className="text-[11px] uppercase font-semibold text-slate-400">Est. Net Margin Impact</div>
                      <div className="text-base font-extrabold text-emerald-700">
                        +{(p.expected_daily_margin_impact_dkk ?? 0).toFixed(1)} DKK/day
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {p.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApplyProposal(p)}
                            disabled={isBusy}
                            className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1 shadow-sm transition hover:opacity-95 disabled:opacity-50"
                            style={{ backgroundColor: PINE }}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {isBusy ? 'Applying...' : 'Approve & Apply'}
                          </button>
                          <button
                            onClick={() => handleRejectProposal(p)}
                            disabled={isBusy}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-200 border border-slate-300 flex items-center gap-1 transition"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            Dismiss
                          </button>
                        </>
                      )}

                      {p.status === 'applied' && (
                        <>
                          <span className="px-2.5 py-1 rounded text-xs font-bold bg-emerald-100 text-emerald-900">
                            ✓ Applied
                          </span>
                          <button
                            onClick={() => handleRollbackProposal(p)}
                            disabled={isBusy}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 flex items-center gap-1 transition"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Rollback
                          </button>
                        </>
                      )}

                      {p.status === 'rolled_back' && (
                        <span className="px-2.5 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-900">
                          Rolled Back
                        </span>
                      )}

                      {p.status === 'rejected' && (
                        <span className="px-2.5 py-1 rounded text-xs font-semibold bg-slate-200 text-slate-700">
                          Dismissed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimplyloanGeoOptimizer;
